/**
 * Generic search function that searches across all columns of JSON data
 * @param {Array} data - Array of objects to search through
 * @param {string} searchTerm - The search term to look for
 * @param {Array} excludeFields - Optional array of field names to exclude from search
 * @returns {Array} - Filtered array of objects that match the search term
 */
export const searchInData = (data, searchTerm, excludeFields = []) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return data;
  }

  const term = searchTerm.toLowerCase().trim();

  return data.filter(item => {
    // Check each property in the object
    return Object.keys(item).some(key => {
      // Skip excluded fields
      if (excludeFields.includes(key)) {
        return false;
      }

      const value = item[key];
      
      // Handle different data types
      if (value === null || value === undefined) {
        return false;
      }

      // Convert to string for searching
      const stringValue = String(value).toLowerCase();
      
      // Check if the search term is found in this field
      return stringValue.includes(term);
    });
  });
};

/**
 * Search function specifically for documents with common exclusions
 * @param {Array} documents - Array of document objects
 * @param {string} searchTerm - The search term to look for
 * @returns {Array} - Filtered array of documents that match the search term
 */
export const searchDocuments = (documents, searchTerm) => {
  // Exclude fields that shouldn't be searched (like IDs, internal fields)
  const excludeFields = [
    'id',
    'is_active',
    'compliance_tool_id',
    'created_at',
    'uploaded_by_user_id',
    'uploaded_by_type',
    'company_id'
  ];

  return searchInData(documents, searchTerm, excludeFields);
};

/**
 * Format date to DD MMM YYYY format (e.g., "15 Aug 2025")
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    // Create date object - this automatically converts UTC to local time
    const date = new Date(dateString);
    
    // Verify the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return '';
    }
    
    // Get day, month, and year (these are in local timezone)
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();
    
    // Month names array (short)
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    // Format as "DD MMM YYYY" (local timezone)
    return `${day} ${monthNames[month]} ${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format Date object to YYYY-MM-DD format without timezone conversion
 * This function preserves the exact date selected by the user
 * @param {Date} date - JavaScript Date object
 * @returns {string} - Date string in YYYY-MM-DD format (e.g., "2002-05-04")
 */
export const formatDateToAPI = (date) => {
  if (!date) return '';
  try {
    // Verify the date is valid
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      console.warn('Invalid date object:', date);
      return '';
    }
    
    // Get year, month, and day from the date object (no timezone conversion)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    // Return formatted date as YYYY-MM-DD
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date to API format:', error);
    return '';
  }
};

/**
 * Get file type from file path
 * @param {string} filePath - File path string
 * @returns {string} - File extension in uppercase
 */
export const getFileType = (filePath) => {
  if (!filePath) return '';
  try {
    const extension = filePath.split('.').pop().toUpperCase();
    return extension;
  } catch (error) {
    console.error('Error getting file type:', error);
    return '';
  }
};

/**
 * Download file using file_path
 * @param {string} filePath - File path from the document object
 * @param {string} fileName - Name to save the file as
 */
export const downloadFile = async (filePath, fileName) => {
  if (!filePath) {
    console.error('No file path provided');
    return;
  }

  try {
    // Import the prefix here to avoid circular dependencies
    const { FILE_DOWNLOAD_PREFIX } = require('./url_helper');
    
    // Construct the full URL
    let downloadUrl;
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      downloadUrl = filePath;
    } else {
      downloadUrl = `${FILE_DOWNLOAD_PREFIX}${filePath}`;
    }

    // Clean double slashes while preserving protocol slashes
    downloadUrl = downloadUrl.replace(/([^:])\/\/+/g, '$1/');

    // Get the auth token for authenticated requests
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    
    // Fetch the file as a blob
    const response = await fetch(downloadUrl, {
      method: 'GET',
      headers: token ? {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/octet-stream'
      } : {
        'Content-Type': 'application/octet-stream'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get the file as a blob
    const blob = await response.blob();
    
    // Create a blob URL
    const blobUrl = window.URL.createObjectURL(blob);
    
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName || 'document';
    link.style.display = 'none';
    
    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the blob URL
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error downloading file:', error);
    // Fallback: try opening in new tab if download fails
    try {
      const { FILE_DOWNLOAD_PREFIX } = require('./url_helper');
      let downloadUrl;
      if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        downloadUrl = filePath;
      } else {
        downloadUrl = `${FILE_DOWNLOAD_PREFIX}${filePath}`;
      }
      
      // Clean double slashes while preserving protocol slashes
      downloadUrl = downloadUrl.replace(/([^:])\/\/+/g, '$1/');
      
      window.open(downloadUrl, '_blank');
    } catch (fallbackError) {
      console.error('Fallback download also failed:', fallbackError);
    }
  }
};

/**
 * View file in new tab/window using file_url
 * @param {string} filePath - File path from the file object
 * @param {string} fileName - Name of the file (optional, for logging)
 */
export const viewFile = async (filePath, fileName) => {
  if (!filePath) {
    console.error('No file path provided');
    return;
  }

  try {
    // Import the prefix here to avoid circular dependencies
    const { FILE_DOWNLOAD_PREFIX } = require('./url_helper');
    
    // Construct the full URL
    let viewUrl;
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      viewUrl = filePath;
    } else {
      viewUrl = `${FILE_DOWNLOAD_PREFIX}${filePath}`;
    }

    // Clean double slashes while preserving protocol slashes
    viewUrl = viewUrl.replace(/([^:])\/\/+/g, '$1/');
    
    // Open in new tab/window
    window.open(viewUrl, '_blank');
  } catch (error) {
    console.error('Error viewing file:', error);
  }
}; 